'use client';

import React from 'react';
import styled, { css } from 'styled-components';
import { useLabels } from '@liftango/ui-tokens';
import { useDensity } from '@liftango/ui-tokens';
import { lightColors } from '@liftango/ui-tokens';
import { pulse } from '../../styled-utils/pulse';

// ── Status configuration ────────────────────────────────────────────
// Each status maps to visual tokens: colors + label.
// Consumers can use the built-in presets, override them, or define
// entirely new statuses via the `config` prop or LabelsProvider.

export interface StatusConfig {
  color: string;
  bg: string;
  dot: string;
  label: string;
  pulse?: boolean;
  bordered?: boolean;
}

// Generic presets — not domain-specific. Consumers extend or override.
// Color values are sourced from tokens/colors.ts so updating a token
// updates every preset in lockstep.
const c = lightColors;
const PRESETS: Record<string, StatusConfig> = {
  active: {
    color: c.status.active.color,
    bg: c.status.active.bg,
    dot: c.status.active.dot,
    label: 'Active',
    pulse: true,
    bordered: true,
  },
  done: {
    color: c.status.done.color,
    bg: c.status.done.bg,
    dot: c.status.done.dot,
    label: 'Done',
  },
  pending: {
    color: c.status.upcoming.color,
    bg: c.status.upcoming.bg,
    dot: c.status.upcoming.dot,
    label: 'Pending',
  },
  warning: {
    color: c.semantic.warn.color,
    bg: c.semantic.warn.soft,
    dot: c.semantic.warn.color,
    label: 'Warning',
  },
  error: {
    color: c.status.noShow.color,
    bg: c.status.noShow.bg,
    dot: c.status.noShow.dot,
    label: 'Error',
  },
  inactive: {
    color: c.status.skipped.color,
    bg: c.status.skipped.bg,
    dot: c.status.skipped.dot,
    label: 'Inactive',
  },
};

export type BadgeSize = 'xs' | 'sm' | 'md';

export interface StatusBadgeProps {
  /** Status key — matches a preset or a custom config */
  status: string;
  /** Override or define the visual config for this status */
  config?: Partial<StatusConfig>;
  /** Override the label text (highest priority) */
  label?: string;
  size?: BadgeSize;
}

// ── Fallback for unknown statuses ───────────────────────────────────
const FALLBACK: StatusConfig = {
  color: c.neutral.inkSoft,
  bg: c.neutral.bg,
  dot: c.neutral.inkMute,
  label: 'Unknown',
};

const PADDING: Record<BadgeSize, string> = { xs: '4px 8px', sm: '5px 10px', md: '6px 12px' };
const DOT_SIZE: Record<BadgeSize, number> = { xs: 5, sm: 6, md: 7 };

const Wrapper = styled.span<{
  $bg: string;
  $color: string;
  $bordered: boolean;
  $padding: string;
  $fontSize: string;
}>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: ${(p) => p.$padding};
  border-radius: ${(p) => p.theme.radius.pill};
  font-size: ${(p) => p.$fontSize};
  font-weight: ${(p) => p.theme.fontWeight.bold};
  font-family: ${(p) => p.theme.fontFamily.ui};
  letter-spacing: 0.3px;
  text-transform: uppercase;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$color};
  line-height: 1.1;
  ${(p) =>
    p.$bordered &&
    css`
      border: 1px solid ${p.$color};
    `}
`;

const Dot = styled.span<{ $dotColor: string; $size: number; $pulse: boolean }>`
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size}px;
  border-radius: 50%;
  background: ${(p) => p.$dotColor};
  flex-shrink: 0;
  ${(p) =>
    p.$pulse &&
    css`
      box-shadow: 0 0 6px ${p.$dotColor};
      animation: ${pulse} ${p.theme.motion.pulse.status} ease-in-out infinite;
    `}
`;

export function StatusBadge({
  status,
  config: configOverride,
  size = 'sm',
  label: labelProp,
}: StatusBadgeProps) {
  const labels = useLabels();
  const density = useDensity();

  // Resolve config: override > preset > fallback
  const preset = PRESETS[status] ?? FALLBACK;
  const resolved: StatusConfig = { ...preset, ...configOverride };

  // Resolve label: prop > labels context > config > capitalize status key
  const resolvedLabel =
    labelProp ??
    labels.status?.[status] ??
    resolved.label ??
    status.charAt(0).toUpperCase() + status.slice(1);

  const fontSize = density.badgeFontSize[size];

  return (
    <Wrapper
      role="status"
      aria-label={`Status: ${resolvedLabel}`}
      $bg={resolved.bg}
      $color={resolved.color}
      $bordered={!!resolved.bordered}
      $padding={PADDING[size]}
      $fontSize={fontSize}
    >
      <Dot $dotColor={resolved.dot} $size={DOT_SIZE[size]} $pulse={!!resolved.pulse} />
      {resolvedLabel}
    </Wrapper>
  );
}

/** Built-in presets, exported so consumers can extend them */
StatusBadge.presets = PRESETS;
